'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { useSession, signOut } from 'next-auth/react'
import { useRouter } from 'next/navigation'

interface AssessmentStatus {
  mbti: boolean
  phq9: boolean
  gad7: boolean
}

export default function DashboardPage() {
  const router = useRouter()
  const { data: session, status } = useSession()
  const [userName, setUserName] = useState('')
  const [assessmentStatus, setAssessmentStatus] = useState<AssessmentStatus>({
    mbti: false,
    phq9: false,
    gad7: false
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (status === 'loading') return
    if (!session?.user) {
      router.push('/auth/signin')
      return
    }

    const userId = session.user.id
    setUserName(session.user.name || session.user.email || '访客')

    const resolveStatus = (base?: AssessmentStatus): AssessmentStatus => ({
      mbti: !!sessionStorage.getItem('mbti-report'),
      phq9: !!sessionStorage.getItem('phq9-emotional-report'),
      gad7: !!sessionStorage.getItem('gad7-emotional-report')
    })

    const fetchAssessmentStatus = async () => {
      try {
        const response = await fetch('/api/assessments/status')
        if (response.ok) {
          const data = await response.json()
          const status = resolveStatus({
            mbti: data.mbti || false,
            phq9: data.phq9 || false,
            gad7: data.gad7 || false
          })
          setAssessmentStatus(status)
          sessionStorage.setItem('assessmentStatus', JSON.stringify(status))
          return
        }
      } catch (error) {
        console.error('Error fetching assessment status:', error)
      }

      const storedStatus = sessionStorage.getItem('assessmentStatus')
      if (storedStatus) {
        try {
          const parsed = JSON.parse(storedStatus)
          setAssessmentStatus(resolveStatus(parsed))
          return
        } catch (_) {
          // ignore parse error and fall back below
        }
      }

      setAssessmentStatus(resolveStatus())
    }

    fetchAssessmentStatus()
      .finally(() => setLoading(false))
  }, [session?.user, status, router])

  if (loading || status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">加载仪表板中...</p>
        </div>
      </div>
    )
  }

  const allCompleted = assessmentStatus.mbti && assessmentStatus.phq9 && assessmentStatus.gad7

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                欢迎，{userName}！
              </h1>
              <p className="text-gray-600">
                请完成以下三项评估。
              </p>
            </div>
            <div className="bg-purple-50 border border-purple-100 rounded-lg p-4 max-w-sm">
              <p className="text-sm text-purple-900 font-semibold mb-2">
                数据清理（不可恢复）
              </p>
              <p className="text-xs text-purple-700 mb-3">
                退出后当前体验账号会立即作废，无法重新登录或找回数据。请在确认已保存所需内容后再执行。
              </p>
              <button
                onClick={() => {
                  const confirmed = window.confirm('清理数据后将立即退出且无法重新登录。确认继续吗？')
                  if (!confirmed) return
                  try {
                    sessionStorage.removeItem('assessmentStatus')
                    sessionStorage.removeItem('mbti-report')
                    sessionStorage.removeItem('phq9-emotional-report')
                    sessionStorage.removeItem('gad7-emotional-report')
                  } catch (_) {
                    // ignore storage errors
                  }
                  signOut({ callbackUrl: '/auth/signin?session=ended' })
                }}
                className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                立即清理并退出
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* MBTI Assessment */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">MBTI 评估</h2>
              {assessmentStatus.mbti && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  已完成
                </span>
              )}
            </div>
            <p className="text-gray-600 text-sm mb-4">
              通过对话完成人格评估。
            </p>
            <Link
              href="/assessments/mbti"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {assessmentStatus.mbti ? '查看结果' : '开始评估'}
            </Link>
          </div>

          {/* 情绪自我觉察量表 */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">情绪自我觉察量表</h2>
              {assessmentStatus.phq9 && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  已完成
                </span>
              )}
            </div>
            <p className="text-gray-600 text-sm mb-4">
              识别近期情绪变化的自我觉察工具。
            </p>
            <Link
              href="/assessments/phq9"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {assessmentStatus.phq9 ? '查看结果' : '开始评估'}
            </Link>
          </div>

          {/* 心理放松状态自评量表 */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">心理放松状态自评量表</h2>
              {assessmentStatus.gad7 && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  已完成
                </span>
              )}
            </div>
            <p className="text-gray-600 text-sm mb-4">
              关注身心紧绷与放松程度的简易自评。
            </p>
            <Link
              href="/assessments/gad7"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {assessmentStatus.gad7 ? '查看结果' : '开始评估'}
            </Link>
          </div>
        </div>

        {allCompleted && (
          <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-800 text-center font-medium">
              ✓ 所有评估已完成！您的结果已保存。
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
